import type { FC } from "react";
import type { LoaderFunction, MetaFunction } from "react-router";
import { data as responseData } from "react-router";
import type { ObjectSchema } from "yup";
import { object, string } from "yup";

import { getBreadcrumbsHomeLabel } from "~/core/transformers/breadcrumb-list.transformer";
import { prepareSeoData } from "~/core/transformers/seo.transformer";
import { sdkContext, settingsContext } from "~/server/get-load-context";
import {
  createLcpImageLinkHeaders,
  resolveLcpImage,
} from "~/server/resolve-lcp-image.server";
import { STATIC_PAGE_CACHE_HEADERS } from "~/shared/cache-headers";
import { getOriginalUrl } from "~/shared/utils/get-original-url";
import { PageConstructor } from "~/widgets/page-constructor";
import { loadPagesByConstructor } from "~/widgets/pages-by-constructor/page-by-constructor.server";

import type { Route } from "./+types/$lang.tournaments.$id";

const createDetailedTournamentValidator = (): ObjectSchema<{
  id: string;
  lang: string;
}> => {
  return object({
    id: string().required(),
    lang: string().required(),
  });
};

export const loader = (async ({ params, context, request }) => {
  const { id, lang } = createDetailedTournamentValidator().validateSync(params);
  const settings = context.get(settingsContext);
  const sdk = context.get(sdkContext);
  const { page, pages } = await loadPagesByConstructor(
    sdk,
    "tournaments",
    lang,
    {
      slug: id,
      segment: "tournaments",
      pageList: "whenRelatedPages",
    },
  );

  const url = new URL(request.url);
  const cmsPath = `${url.protocol}//${url.host}`;
  const lcpImage = resolveLcpImage(page, cmsPath);
  const responseHeaders = new Headers(STATIC_PAGE_CACHE_HEADERS);

  for (const linkHeader of createLcpImageLinkHeaders(lcpImage)) {
    responseHeaders.append("Link", linkHeader);
  }

  return responseData(
    {
      pageData: page,
      pages,
      originalUrl: getOriginalUrl(request.url),
      languages: settings?.languages,
      lcpImage,
    },
    {
      headers: responseHeaders,
    },
  );
}) satisfies LoaderFunction;

export function headers({
  loaderHeaders,
  actionHeaders,
}: Route.HeadersArgs): Headers {
  return loaderHeaders ?? actionHeaders;
}

export const meta: MetaFunction<typeof loader> = ({
  data,
  params,
  matches,
}) => {
  return prepareSeoData({
    pageSeo: data?.pageData?.page.seo,
    fallbackSeo: data?.pageData?.fallbackSeo?.seo,
    originalUrl: `${data?.originalUrl}`,
    currentLang: params?.lang ?? "",
    languages: data?.languages ?? [],
    matches,
    breadcrumbs: data?.pageData
      ? {
          data: data.pageData,
          homeLabel: getBreadcrumbsHomeLabel(matches),
        }
      : undefined,
  });
};

const Page: FC<Route.ComponentProps> = ({
  loaderData: { pageData, pages },
}) => {
  return pageData && <PageConstructor data={pageData} pages={pages} />;
};

export default Page;
